import { hideLoader } from "action/loader/hideLoader";
import { showLoader } from "action/loader/showLoader";
import { openModal } from "action/modal/openModal";
import { setUseVehicleSelectionModal } from "action/utils/setUseVehicleSelectionModal";
import AmpService from "analytics/amplitude/amplitudeEvents";
import KlaviyoTrackingService from "analytics/klaviyo/klaviyoEvents";
import { getCategoryCanonicalUrlV2 } from "api/category/getCategoryCanonicalUrlV2";
import { IMakeModelTierActualResponse, IMakeModelTierPageContent } from "api/interfaces/get-page-details/tier-make-model.type";
import { ITierActualResponse, ITierPageContent } from "api/interfaces/get-page-details/tier-page.type";
import CategoryMetaHead from "component/category-tier/CategoryMetaHead";
import SsrProductContainer from "component/category/SsrProductContainer";
import { MAKE_MODEL_YEAR, MAKE_YEAR_MODEL, YEAR_MAKE_MODEL } from "component/vehicle-form/constants";
import matchMediaConst from "constant/matchmedia";
import { FILTER_TYPE } from "constant/product-filter.action";
import usePrevious from "hoc/usePrevious";
import { useCustomScreenSize } from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { IVehicle } from "interfaces/session/IVehicle";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import qs from "query-string";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendCategoryViewEvent } from "util/analytics/sendCategoryViewEvent";
import { DESKTOP_ITEM_COUNT, MOBILE_ITEM_COUNT } from "util/category/category-utils";
import { isTires } from "util/category/is-tires";
import { encode } from "util/helper/encode";
import { isActualTier0, isOEHP } from "util/helper/helper";
import { getLSItem, removeLSItem } from "util/localStorageUtils";
import { deepEqual } from "util/lodash/deepEqual";
import { filterObjectKeys } from "util/lodash/filterObjectKeys";
import { strongPick } from "util/lodash/strongPick";
import { isMerchandiseCategory } from "util/product/apparel";
import { TIER_MAKE_MODEL_PAGES } from "util/product/product-constants";
import { refreshData } from "util/refreshData";
import { scrollTo } from "util/scroll-helper";
import { getFragmentParams } from "util/url/getFragmentParams";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import { b2bUrl } from "util/url/static-urls";
import { createVehicleDescription } from "util/vehicle/createVehicleDescription";
import { equalVehicles } from "util/vehicle/equal-vehicles";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import useNonInitialEffect from "../lib/hooks/useNonInitialEffect";
import RattingScript from "../third-party/sitejabber/RattingScript";
import { QueryParams, ValidQueryParams, validQueryParams } from "./constants/constants";

let CSR_DONE = false; // client side refesh

interface IProps {
  actualResponse: ITierActualResponse & IMakeModelTierActualResponse;
  staticContent: ITierPageContent & IMakeModelTierPageContent;
  pageType: string;
  isSSR: boolean;
  serverRequestUrl: string;
}

function CategoryTierContainer(props: IProps) {
  const {
    actualResponse, staticContent, pageType, isSSR, serverRequestUrl, locale,
  } = props;
  const { breadCrumbsV2, useVehicleSelectionOptionsModal = false } = actualResponse;
  const { makeModelTiers } = staticContent;
  const {
    hideMetaHead = false, comonTop, innerQuery = {}, yearMakeModelPage,
  } = props;
  const [clientSidelatestData, setLatestData] = useState({});
  const {
    urlInformation,
    useVehicleSelectionOptionsModal: useVehicleSelectionOptionsModalRedux,
  } = useSelector((store: RootState) => store.utils);
  const isTier0 = isActualTier0(urlInformation);
  const isMerchandiseCat = isMerchandiseCategory(urlInformation);

  const selectedVehicle = useSelector((state: RootState) => state.garage.selectedVehicle);
  const vehicleDescription = createVehicleDescription(selectedVehicle, true, false, true);

  const postalCode = useSelector((state: RootState) => state.shoppingCart.postalCode);
  const { businessCustomer } = useSelector((store: RootState) => store.authenticationState);

  const [updatedData, setUpdatedData] = useState(Object.keys(clientSidelatestData).length > 0 ? clientSidelatestData : actualResponse || {});

  const {
    fitmentOptionsPrompt = [],
    fitmentOptionsDetails = {},
    productsAggregation = {},
  } = updatedData;

  const { layout: productLayout, currentPageType } = useSelector((state: RootState) => state.utils);
  const modal = useSelector((state: RootState) => state.modal);
  const { t } = useTranslation(["common", "category-map"]);
  // added urlInformation in prevSelectedVehicle for make_model_category page.
  // goto /ford-f150-front-brake-rotor then select year.
  // const prevSelectedVehicle = usePrevious(selectedVehicle || pick(urlInformation, ["make", "model", "year"]));
  const prevSelectedVehicle = usePrevious(selectedVehicle);

  const loadingGarage = useSelector((state: RootState) => state.garage.isLoading);
  const { selectedId } = useSelector((store: RootState) => store.modal);
  const dispatch = useDispatch();
  const {
    products, categoryAggregation, nbcFilterableAttributes = [], ...filterOptions
  } = productsAggregation;
  const tiersWithUrls = clientSidelatestData.tiersWithUrls || props.actualResponse.tiersWithUrls;
  // preference is given to  client side data bcz on refresh server side data is not up to date

  useEffect(() => {
    setUpdatedData((Object.keys(clientSidelatestData).length > 0 ? clientSidelatestData : actualResponse));
  }, [clientSidelatestData]);

  useEffect(() => {
    setUpdatedData(actualResponse);
  }, [actualResponse]);

  useEffect(() => {
    dispatch(setUseVehicleSelectionModal(useVehicleSelectionOptionsModal));

    return () => {
      dispatch(setUseVehicleSelectionModal(false));
    };
  }, [useVehicleSelectionOptionsModal]);

  const {
    tier0, tier1, tier2, tier3, make, model, year,
  } = urlInformation || {};
  let categories = [];
  const tier1s = categoryAggregation?.tier0s.find((el) => el.bucket?.toLowerCase() === tier0?.toLowerCase())?.tier1s;
  const updatedCategories = isTier0 ? tier1s : tier1s?.find((el) => el.bucket.toLowerCase() === tier1.toLowerCase())?.tier2s;
  categories = updatedCategories || [];

  // useSafeRouter causing infinite loop, on client side.
  // const { query } = useSafeRouter();
  const router = useRouter();
  const { query, asPath } = router;
  const { pathname, ...queryFilters } = query;
  const {
    fitments: ft, availability: av, priceRanges: pr, manufacturers: m, pageNumber: pg, sortBy: sb,
  } = query || {};
  const isMobile = useCustomScreenSize({
    maxWidth: matchMediaConst.XS_MAX,
  });

  const currCategory = {
    tier0: urlInformation.tier0 || query.tier0,
    tier1: urlInformation.tier1 || query.tier1,
    tier2: urlInformation.tier2 || query.tier2,
    tier3: urlInformation.tier3 || query.tier3,
  };

  const isOEPage = isOEHP(asPath);

  function clientSideRefresh(vehicle: IVehicle) {
    return refreshData({
      pathname,
      isMobile,
      selectedVehicle: vehicle,
      serverRequestUrl,
      actualResponse,
    });
  }

  // isMakeModelTierPage is to set not to ask year again, if present in selected vehicle
  function openVehiceSelectorPopup(csrFOPrompt = [], csrFODetails, isMakeModelTierPage = false) {
    // Don't open vehicle selector for B2B customer / Merchandise category / OE page
    if (businessCustomer || isMerchandiseCat || isOEPage) {
      return;
    }

    const parsedQP = qs.parse(location.search);

    const { year: fragmentYear, make: fragmentMake, model: fragmentModel } = getFragmentParams();

    const yearPesent = urlInformation.year || parsedQP.year || fragmentYear || (isMakeModelTierPage ? selectedVehicle?.year : undefined);
    const makePresent = urlInformation.make || parsedQP.make || fragmentMake;
    const modelPresent = urlInformation.model || parsedQP.model || fragmentModel;

    const categoryforVehicleDetails = {
      tier0: urlInformation.tier0,
      tier1: urlInformation.tier1 || parsedQP.tier1,
      tier2: urlInformation.tier2 || parsedQP.tier2,
    };

    // when make, model, year are present then open popup when T1 is also present.
    if (makePresent && modelPresent && yearPesent && !categoryforVehicleDetails?.tier1) {
      return;
    }

    let selectionOrder = YEAR_MAKE_MODEL;
    if (makePresent && modelPresent && !yearPesent) {
      selectionOrder = MAKE_MODEL_YEAR;
    } else if (makePresent && !modelPresent && !yearPesent) { // https://dev.partsavatar.ca/cv-boot?make=HONDA
      selectionOrder = MAKE_YEAR_MODEL;
    }

    const isCarCareCategory = tier1?.toLowerCase() === "car care & detailing";
    if (staticContent?.pageDetails?.tier0 === "Tools & Equipment" || isCarCareCategory) {
      return;
    }

    const vehicle = { year: yearPesent, make: makePresent, model: modelPresent };
    const skipVehicleSelectorModal = ["checkFitmentModel", "shoppingCart"].includes(selectedId);

    if ((!validateVehicle(vehicle) || (fitmentOptionsPrompt && fitmentOptionsPrompt.length > 0)) && !skipVehicleSelectorModal) {
      setTimeout(() => {
        dispatch(openModal("vehicleSelector", {
          title: t("Complete your vehicle details"),
          // years,
          defaultMake: makePresent,
          defaultModel: modelPresent,
          defaultYear: yearPesent,
          selectionOrder,
          category: categoryforVehicleDetails,
          fitmentOptionsPrompt: csrFOPrompt,
          fitmentOptionsDetails: csrFODetails,
          clientSideRefresh,
          setData,
        }));
      }, 0);
    }
  }

  const isTiresCategory = () => isTires(currCategory.tier1) && (!currCategory.tier2 || isTires(currCategory.tier2));

  useEffect(() => {
    const parsedQP = qs.parse(location.search);

    const { year: fragmentYear } = getFragmentParams();
    const yearPesent = urlInformation.year || parsedQP.year || fragmentYear;
    // wait for garage to load.
    if (loadingGarage) {
      return;
    }
    const isTiresCat = isTiresCategory();
    /**
     * Open VehiceSelectorPopup on
     * 1. non Tires category ex: https://partsavatar.ca/axles-drivetrain
     * 2. Tires category when make, model is present. ex: https://partsavatar.ca/honda-civic-tire
     */
    // Don't open vehicle selector when client side refresh is not done
    if (CSR_DONE) {
      openVehiceSelectorPopup(fitmentOptionsPrompt, fitmentOptionsDetails);
    }
  }, [loadingGarage, updatedData]);

  function reload(options: any) {
    const { loader = false, skipVehicleSelectorModal = false } = options || {};
    if (loader) {
      dispatch(showLoader());
    }
    /**
       * Will be called for change/select of
       * 1. vehicle
       * 2. engine
       * 3. SSR page don't have vehicle, but user state have vehicle, so take user to vehicle + category page
       *    ex. go to
       *        1. go to https://dev.partsavatar.ca/2010-honda-civic-axles-drivetrain
       *        2. close browser window
       *        3. go to https://dev.partsavatar.ca/axles-drivetrain
       *        4. You will automatically redierct to /2010-honda-civic-axles-drivetrain
       * 4. User state have selected engine and SSR page called
       *    ex. go to
       *        1. go to https://dev.partsavatar.ca/2010-honda-civic-axles-drivetrain
       *        2. select engine and close browser window
       *        3. again go to https://dev.partsavatar.ca/2010-honda-civic-axles-drivetrain
       *        4. Engine filter will be applied on client side
       */

    // Seems no use of calling refreshData here
    if (equalVehicles(prevSelectedVehicle, selectedVehicle)) {
      // engine is added
      // vehicle remains same but fitment option changes
      if (!equalVehicles(prevSelectedVehicle, selectedVehicle, true)) {
        refreshData({
          pathname,
          isMobile,
          selectedVehicle,
          skipVehicleSelectorModal,
          serverRequestUrl,
          actualResponse,
        })
          .then(({ keysToUpdate, skipVehicleSelectorModal: skipModal }) => {
            setData(keysToUpdate, skipModal);
            // dispatch(hideLoader());
          });
      } else {
        dispatch(hideLoader());
      }
    } else {
      getCategoryCanonicalUrlV2(currCategory, selectedVehicle, null)
        .then((res: any) => {
          const { url, success, queryParams } = res.data;
          if (success) {
            const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
              make: ma, model: mo, year: yr, ...buildQueryParams
            } = queryFilters;
            delete buildQueryParams.pageNumber;
            delete buildQueryParams.availabilities;
            const { href, asPath } = getHrefAndPath({
              query: { ...queryParams, ...buildQueryParams }, asPath: url,
            }, false, true);
            dispatch(hideLoader());
            router.replace(href, asPath);
          }
        });
    }
  }

  useEffect(() => {
    if (loadingGarage) {
      return;
    }
    if (!CSR_DONE && isSSR) {
      CSR_DONE = true;
      // client side data refesh single time, for SSR page only
      // as SSR page might be cached and user have stale prices or product info
      refreshData({
        pathname,
        isMobile,
        selectedVehicle,
        serverRequestUrl,
        actualResponse,
      })
        .then(({ keysToUpdate, skipVehicleSelectorModal }) => {
          setData(keysToUpdate, skipVehicleSelectorModal, TIER_MAKE_MODEL_PAGES.includes(currentPageType));
        });
    }
  }, [loadingGarage]);

  function setData(keysToUpdate: any, skipVehicleSelectorModal: boolean, isMakeModelTierPage = false) {
    setLatestData(keysToUpdate);
    const productSku = getLSItem("category-scroll-to-product");
    if (productSku) {
      scrollTo(`product-card-${productSku}`, {
        duration: 800,
        delay: 0,
        smooth: true,
        offset: -400,
      });
      scrollTo(`product-grid-card-${productSku}`, {
        duration: 800,
        delay: 0,
        smooth: true,
        offset: -100,
      });
      removeLSItem("category-scroll-to-product");
    }
    if (!skipVehicleSelectorModal) {
      openVehiceSelectorPopup(keysToUpdate.fitmentOptionsPrompt, keysToUpdate.fitmentOptionsDetails, isMakeModelTierPage);
    }
    dispatch(hideLoader());
  }

  useNonInitialEffect(() => {
    // when vehicle change is initiated from searchModal,
    // then don't don't trigger categorty page flow,
    // insted it will go to search page OR url given from serch autocomplete
    const searchInputActive = Boolean(document.querySelector("[data-search-active]"));

    if (modal.selectedId === "searchModal" || isOEPage || searchInputActive) {
      return;
    }

    const { year: fragmentYear } = getFragmentParams();
    // don't reload for fragmentYear
    if (prevSelectedVehicle == null && fragmentYear) {
      return;
    }
    if (!deepEqual(prevSelectedVehicle, selectedVehicle)) {
      // skipVehicleSelectorModal when fitment options are being changed
      // debugger;
      const isDiffVehicle = !equalVehicles(prevSelectedVehicle, selectedVehicle);
      const skipVehicleSelectorModal = validateVehicle(prevSelectedVehicle) && !equalVehicles(prevSelectedVehicle, selectedVehicle, true);

      if (businessCustomer && isDiffVehicle) {
        const { href, asPath } = getHrefAndPath({ asPath: b2bUrl });
        router.push(href, asPath);
        return;
      }
      reload({ loader: true, skipVehicleSelectorModal });
    }
  }, [vehicleDescription]);

  const { pageDetails = {} } = staticContent || {};

  const breadcrumbData = filterObjectKeys(pageDetails, ["tier0", "tier1", "tier2", "tier3"]);
  const categoryObj = {
    tier0: breadcrumbData.tier0,
    tier1: breadcrumbData.tier1 || queryFilters.tier1,
    tier2: breadcrumbData.tier2 || queryFilters.tier2,
    tier3: breadcrumbData.tier3 || queryFilters.tier3,
  };

  useEffect(() => {
    const categoryEventProps = {
      ft, av, pr, m, make, model, year, pg, sb, ...categoryObj,
    };
    sendCategoryViewEvent(categoryEventProps);
    setLatestData({});
  }, [query]);

  const clientSideProductAggregation = productsAggregation;
  useEffect(() => {
    try {
      if (!updatedData || Object.keys(updatedData).length === 0) {
        return;
      }

      const parsed = qs.parse(location.search);

      const { year: fragmentYear, make: fragmentMake, model: fragmentModel } = getFragmentParams();

      const queryParams = strongPick<QueryParams, ValidQueryParams>(parsed, validQueryParams);

      const numberOfProducts = isMobile ? MOBILE_ITEM_COUNT : DESKTOP_ITEM_COUNT;

      const t1 = queryParams.tier1;

      const t2 = queryParams.tier2;

      const t3 = queryParams.tier3;

      const parsedYear = fragmentYear || queryParams.year;

      const parsedMake = fragmentMake || queryParams.make;

      const parsedModel = fragmentModel || queryParams.model;

      const pageNumber: number = parsed.pageNumber || 1 as any;

      AmpService.categoryPageViewed({
        tier0,
        tier1,
        tier2,
        tier3,
        query: {
          tier1: t1 || "NOT_PRESENT",
          tier2: t2 || "NOT_PRESENT",
          tier3: t3 || "NOT_PRESENT",
          year: parsedYear || "NOT_PRESENT",
          make: parsedMake || "NOT_PRESENT",
          model: parsedModel || "NOT_PRESENT",
        },
        top5ProductSkus: products?.products?.slice(0, 5)?.map((p: { sku: string }) => p.sku || "NOT_PRESENT"),
        brandsCount: clientSideProductAggregation?.manufacturers
          ?.reduce((prev: any, curr: {
            count: number,
            bucket: string,
          }) => ({
            ...prev,
            [curr.bucket]: curr.count,
          }), {}),
        availabilityCount: {
          inStock: clientSideProductAggregation?.availabilities?.find((b) => b.bucket === "In Stock")?.count,
          outOfStock: clientSideProductAggregation?.availabilities?.find((b) => b.bucket === "Out of Stock")?.count,
        },
        totalProductsCount: products.totalHits,
        totalPages: Math.ceil(products.totalHits / numberOfProducts),
        pageNumber,
        selectedMake: parsedMake,
        selectedModel: parsedModel,
        selectedYear: parsedYear,
        fitmentOptions: selectedVehicle?.fitmentOptions,
        postalCode,
        sortBy: queryParams.sortBy ?? "NOT_PRESENT",
        viewType: productLayout,
        // experimentCategoryPage: getLSItem("exp-category-T0-T1") ? "grid-cards" : "products",
      });

      if (!products?.products?.length) {
        AmpService.categoryPageNoProducts({
          category: categoryObj,
          query: Object.fromEntries(Object.entries(queryParams).filter(([_, value]) => Boolean(value))),
          selectedVehicle,
          url: asPath,
        });
      }
      const category = {
        tier0, tier1, tier2, tier3,
      };
      KlaviyoTrackingService.categoryViewed(category, selectedVehicle, router.asPath, products?.products);
    } catch (err) {
      console.log(err);
    }
  }, [clientSideProductAggregation]);

  // if (!updatedData || Object.keys(updatedData).length === 0) {
  //   return null;
  // }

  return (
    <>

      <RattingScript />
      {
        !hideMetaHead && (

          <CategoryMetaHead
            {...{
              query,
              tier3,
              tier2,
              tier1,
              tier0,
              make,
              model,
              year,
              yearMakeModelPage,
              totalHits: products.totalHits || 0,
              products: products.products || [],
              // bannerData: categoryDetails,
              tiersWithUrls,
              staticContent,
              pageSize: isMobile ? MOBILE_ITEM_COUNT : DESKTOP_ITEM_COUNT,
              breadCrumbsList: breadCrumbsV2[0],
              locale,
            }}
          />
        )
      }

      <SsrProductContainer
        url={props.url || "/[pathname]"}
        hideQsParams={["t0", "make", "model", "year", "pathname"]}
        filterValues={{
          [FILTER_TYPE.TIER0]: encode(tier0, "(_|-)", " "),
          [FILTER_TYPE.TIER1]: tier1,
          [FILTER_TYPE.TIER2]: tier2,
          [FILTER_TYPE.TIER3]: tier3,
          [FILTER_TYPE.MANUFACTURER]: m ? [].concat(m) : [],
          [FILTER_TYPE.PRICE]: pr ? [].concat(pr) : [],
          [FILTER_TYPE.AVAILABILITY]: av ? [].concat(av) : [],
          [FILTER_TYPE.FITMENT]: ft ? [].concat(ft) : [],
        }}
        immutableFilters={[FILTER_TYPE.TIER0]}
        // bannerData={categoryDetails}
        breadcrumbData={breadcrumbData}
        tiersWithUrls={tiersWithUrls}
        // urlInformation={urlInformation}
        staticContent={staticContent}
        makeModelTiers={makeModelTiers}
        categoryAggregation={categoryAggregation}
        filterParams={{
          vehicle: { enabled: false },
        }}
        pageType={pageType}
        {...{
          breadCrumbsV2,
          innerQuery,
          comonTop,
          filterOptions,
          nbcFilterableAttributes,
          productList: {
            products: products.products || [],
            totalHits: products.totalHits || 0,
          },
        }}
        categories={categories}
        fitmentOptionsDetails={fitmentOptionsDetails}
        fitmentOptionsPrompt={fitmentOptionsPrompt}
      />
    </>
  );
}
export default CategoryTierContainer;
